<script setup>
import LegalPage from "./LegalPage.vue";
</script>

<script>
export default { inheritAttrs: false };
</script>

<template>
  <LegalPage
    title="Website Terms of Service"
    version=""
    effective="October 11, 2026"
  >
    <section id="contacts">
      <p>
        <strong>Contracting Entities:</strong> TalentSage LLC (US) / TalentSage
        UK Limited (UK/EU/Intl)<br />
        <strong>Primary Contact &amp; Privacy Office:</strong> Head of Privacy |
        Privacy@TalentSage.com<br />
        <strong>EEA/Swiss Representative &amp; DSA:</strong> DataRep
        (datarequest@datarep.com / dsa@datarep.com)<br />
        <strong>Independent Recourse &amp; Oversight:</strong> BBB National
        Programs DPF Services | US FTC
      </p>
    </section>

    <!-- 1 -->
    <section id="acceptance">
      <h2>1. Acceptance, Scope &amp; Service Delivery Models</h2>
      <div class="plain">
        <span class="plain-label">In plain English</span>
        <p>
          Welcome to TalentSage! These Terms govern your use of our platforms,
          software, assessments, and coaching services. We serve four distinct
          types of users: corporate employer programs, independent coaches,
          direct TalentSage programs, and individual B2C users or Data Passport
          account holders. By accessing our platform or purchasing our
          services, you agree to these legally binding terms.
        </p>
      </div>

      <h3>1.1 Scope of Platform Services &amp; Delivery Models</h3>
      <p>
        TalentSage provides a Software-with-a-Service (SwaS) leadership
        development platform, psychometric diagnostic engines, executive
        coaching, and learning resources. Platform services operate across four
        distinct commercial delivery models:
      </p>
      <p>
        <strong>Model 1 (B2B Enterprise).</strong> Employer-sponsored leadership
        development, 360º feedback, and diagnostic programs governed by a
        corporate Enterprise Master Agreement Suite (MSA) or Sales Order.
      </p>
      <p>
        <strong>Model 2 (Independent Coaching Practices).</strong> Platform
        access, diagnostic engines, and report generation tools utilized by
        independent coaches, consultants, or accredited facilitators for their
        own clients.
      </p>
      <p>
        <strong>Model 3 (Direct TalentSage Programs).</strong> End-to-end cohort
        or 1-on-1 development programs delivered directly by TalentSage
        internal faculty, executive coaches, and program managers.
      </p>
      <p>
        <strong>Model 4 (Direct Individual Subscriptions &amp; Data Passport
        Accounts).</strong> Individual users, sole proprietors, or transitioning
        corporate participants purchasing direct annual platform seats, survey
        tokens, self-assessments, or maintaining an ongoing personal Data
        Passport Account.
      </p>

      <h3>1.2 Multi-Tier User &amp; Purchaser Categories</h3>
      <p>
        To ensure appropriate legal rights and operational boundaries, these
        Terms establish specific rules for four purchaser and user categories:
      </p>
      <p>
        <strong>a) Small Businesses &amp; Team Buyers (Administrators).</strong>
        Organizations, team leads, or HR managers purchasing platform seats,
        diagnostic tokens, or workshops on behalf of employees or small business
        teams. Administrators warrant corporate authority to register users,
        accept responsibility for account security, and act as the Data
        Controller (or authorized agent) for their cohort.
      </p>
      <p>
        <strong>b) Independent Coaches &amp; Accreditees.</strong> Independent
        practitioners purchasing diagnostic tools or debriefing services for
        third-party clients. Coaches operate as legal Data Sub-processors, bound
        by Train-the-Trainer (TTT) accreditation limits, non-solicitation, and
        closed-AI restrictions.
      </p>
      <p>
        <strong>c) Corporate &amp; Sponsored Participants (Data Subjects).</strong>
        Employees, coachees, raters, and program attendees accessing the
        platform under enterprise or coaching programs.
      </p>
      <p>
        <strong>d) Direct B2C Consumers &amp; Data Passport Holders.</strong>
        Individuals buying services directly for personal career development or
        maintaining transferred personal accounts following corporate
        participation.
      </p>
    </section>

    <!-- 2 -->
    <section id="entities">
      <h2>2. Dual-Entity Structure, Account Registration &amp; Named-Seat Licensing</h2>
      <div class="plain">
        <span class="plain-label">In plain English</span>
        <p>
          TalentSage operates through two legal entities. If you are located in
          the United States, you contract with TalentSage LLC (governed by
          Delaware/Texas law). If you are outside the US (UK, EU,
          International), you contract with TalentSage UK Limited (governed by
          English law). Account seats are dedicated to specific named users and
          cannot be shared or transferred without authorization.
        </p>
      </div>

      <h3>2.1 Dual Contracting Entity Allocation</h3>
      <p>
        Your contracting entity, governing law, and payment currency are
        determined strictly by your geographic location and transaction
        currency:
      </p>
      <p>
        <strong>a) TalentSage LLC (US).</strong> For users, buyers and
        organizations located in the United States, platform services are
        contracted through TalentSage LLC (Delaware Registration #4343380;
        Headquartered in Allen, TX). Governed by the laws of Delaware and Texas
        venue. Transactions billed in USD ($).
      </p>
      <p>
        <strong>b) TalentSage UK Limited (UK/EU/Intl).</strong> For users,
        buyers and organizations located in the United Kingdom, European Union,
        Switzerland, or internationally outside the US, platform services are
        contracted through TalentSage UK Limited (Company #14668801; Registered
        Office: Huddersfield, UK). Governed by the laws of England and Wales.
        Transactions billed in GBP (£) or EUR (€).
      </p>

      <h3>2.2 Named-Seat Licensing &amp; Account Security</h3>
      <p>
        All platform access is licensed on a strict Named-Seat basis. User
        credentials (usernames and passwords) are assigned to specific
        individuals and may not be shared, pooled, or transferred across
        multiple employees or clients. Administrators and individual users are
        solely responsible for maintaining credential confidentiality and must
        immediately notify Privacy@TalentSage.com of any suspected breach.
      </p>
    </section>

    <!-- 3 -->
    <section id="coach-consent">
      <h2>3. Coach Sub-Processor Status &amp; Explicit Consent Gate</h2>
      <div class="plain">
        <span class="plain-label">In plain English</span>
        <p>
          Whether provided by your company, hired on your own or supplied by
          TalentSage, Coaches operate as legal Data Sub-processors. A Coach
          CANNOT view your diagnostic results, 360° rater breakdowns or other
          information unless YOU explicitly grant them access through your
          platform settings.
        </p>
      </div>

      <h3>3.1 Sub-Processor Legal Status</h3>
      <p>
        Whether assigned by a sponsoring employer, engaged independently by a
        participant, or provided directly by TalentSage, executive coaches,
        consultants and accredited facilitators operate legally as Data
        Sub-processors under applicable data protection laws (GDPR / UK GDPR /
        DPF). All are bound by platform confidentiality, data security and
        non-disclosure standards.
      </p>

      <h3>3.2 Mandatory Participant Explicit Consent Gate</h3>
      <p>
        Access to participant diagnostic data, 360º rater breakdowns,
        psychometric scores or qualitative feedback requires explicit,
        affirmative permission through the platform user interface, which the
        participant may modify or revoke at any time. Detailed sub-processor
        terms are set forth in our Privacy Policy.
      </p>
    </section>

    <!-- 4 -->
    <section id="conduct">
      <h2>4. User Conduct, Open-Text Fair Use Standards &amp; Anti-Scraping</h2>
      <div class="plain">
        <span class="plain-label">In plain English</span>
        <p>
          We foster a constructive, professional environment for leadership
          development. Survey respondents and raters must provide honest,
          respectful feedback. Inappropriate or improper comments are strictly
          prohibited. TalentSage is not responsible for user comments, but we
          reserve the right to remove non-compliant content and suspend
          offending accounts. Automated web scraping or reverse-engineering of
          our diagnostic engines is prohibited.
        </p>
      </div>

      <h3>4.1 Open-Ended Feedback Code of Conduct (Fair Use)</h3>
      <p>
        When submitting qualitative feedback, peer reviews, 360º comments or
        coaching journal entries, users must adhere to strict professional
        conduct standards. Users are expressly prohibited from entering content
        that is:
      </p>
      <ol>
        <li>
          In violation of professional workplace standards, unlawful, or
          inappropriate;
        </li>
        <li>
          Infringing upon third-party intellectual property, trade secrets, or
          contractual non-disclosure obligations;
        </li>
        <li>
          Containing unauthorized personally identifiable information (PII) of
          uninvolved third parties.
        </li>
      </ol>

      <h3>4.2 Rater Anonymity &amp; Disclaimer of TalentSage Liability</h3>
      <p>
        Qualitative comments provided by 360º raters (excluding direct
        managers) are aggregated into minimum reporting thresholds (minimum of 3
        raters per rater category) to protect rater anonymity. Qualitative
        feedback reflects solely the personal opinions of individual submitters.
        TalentSage does not endorse, verify, or assume liability for
        user-submitted comments.
      </p>

      <h3>4.3 Content Moderation, Account Revocation &amp; Anti-Scraping</h3>
      <p>
        TalentSage reserves the right, at its sole discretion, to redact
        non-compliant qualitative comments or immediately suspend platform
        access for any user who violates these conduct standards, without refund
        or liability. Furthermore, automated web scraping, data extraction,
        spidering, or prompt-engineering reverse analysis of TalentSage
        diagnostic engines is strictly prohibited.
      </p>
    </section>

    <!-- 5 -->
    <section id="ai">
      <h2>5. Closed AI Governance, Zero Model Training &amp; Session Recording Bans</h2>
      <div class="plain">
        <span class="plain-label">In plain English</span>
        <p>
          Your data is 100% private. We NEVER use your survey answers, 360º
          results, or coaching notes to train public AI models. To protect
          psychological safety, third-party AI meeting bots (like Otter or
          Fathom) and unauthorized video recordings are strictly prohibited
          during coaching sessions and workshops.
        </p>
      </div>

      <h3>5.1 Zero Public Model Training Guarantee</h3>
      <p>
        Personal data, survey responses, 360º rater feedback, psychometric
        scores and coaching journals are NEVER processed, sold, published or
        retained to train public or open-source machine learning models, as set
        forth in our Privacy Policy.
      </p>

      <h3>5.2 Public AI Prohibitions</h3>
      <p>
        Users, coaches and administrators shall not copy, paste or ingest
        proprietary TalentSage diagnostic frameworks, 360º question sets or
        generated report content into open consumer AI tools.
      </p>

      <h3>5.3 Session Recording &amp; AI Meeting Bot Ban</h3>
      <p>
        To ensure psychological safety during executive coaching and virtual
        workshops, third-party AI meeting assistants, note-taking bots (e.g.,
        Fathom, Otter.ai, Firefly) and unauthorized audio/video recording
        software are strictly banned during live platform sessions. Where an
        enterprise sponsor purchases an Extended Use Upgrade permitting session
        recording (e.g. for absent participants or organizational sharing),
        recording shall only occur with advance notice to participants before
        recording commences.
      </p>
    </section>

    <!-- 6 -->
    <section id="purchases">
      <h2>6. Commercial Purchases, 12-Month Breakage Rule &amp; Cancellation Policies</h2>
      <div class="plain">
        <span class="plain-label">In plain English</span>
        <p>
          All prepaid seats, survey tokens, and coaching hours expire 12 months
          after purchase without rollover. Digital reports deliver immediate
          value and are non-refundable. Coaching sessions require at least 2
          business days (48 hours) notice to cancel or reschedule, though every
          user gets a one-time Emergency Grace waiver.
        </p>
      </div>

      <h3>6.1 12-Month Token &amp; Seat Breakage Rule</h3>
      <p>
        All purchased diagnostic tokens, survey licenses, platform user seats,
        and prepaid coaching/workshop hours must be utilized within twelve (12)
        months from the date of purchase or order launch. Unused tokens or hours
        automatically expire after 12 months (Breakage) without credit,
        rollover, or refund.
      </p>

      <h3>6.2 Digital Purchases &amp; Refund Policy</h3>
      <p>
        Diagnostic reports, online assessments, and digital subscriptions
        deliver immediate intellectual property and data processing value upon
        access. All digital purchases are non-refundable once report generation
        or survey access has been initiated.
      </p>

      <h3>6.3 Strict 48-Hour Coaching Cancellation Policy</h3>
      <p>
        Rescheduling or cancelling a 1-on-1 executive coaching session requires
        at least two (2) full business days (48 business hours, excluding
        weekends and public holidays) advance written notice to the assigned
        coach and Privacy@TalentSage.com.
      </p>
      <p>
        <strong>Emergency Grace Waiver.</strong> Each participant is granted one
        (1) Emergency Grace Waiver per program calendar year, allowing a single
        late cancellation without penalty.
      </p>
      <p>
        <strong>Same-Week Shifting.</strong> Shifting a session within the same
        business week is permitted at no charge, subject strictly to coach
        calendar availability.
      </p>
      <p>
        <strong>Late Cancellation Fee.</strong> Cancellations with less than 48
        business hours notice (outside grace limits) result in full forfeiture
        of the session fee/credit.
      </p>
    </section>

    <!-- 7 -->
    <section id="ip">
      <h2>7. Intellectual Property &amp; Background Diagnostic Engines</h2>
      <div class="plain">
        <span class="plain-label">In plain English</span>
        <p>
          TalentSage owns all proprietary diagnostic engines, 360º algorithms,
          courseware and platform technology. When you complete an assessment,
          you receive a license to use your generated report for your personal
          career development or internal corporate training.
        </p>
      </div>

      <h3>7.1 Retention of Proprietary Background IP</h3>
      <p>
        TalentSage retains exclusive ownership of all background technology,
        psychometric algorithms, 360º feedback frameworks, survey
        questionnaires, software code, courseware, and trademarked materials.
        Nothing in these Terms transfers ownership of underlying Intellectual
        Property to users, coaches, or enterprise clients.
      </p>

      <h3>7.2 User License to Generated Reports</h3>
      <p>
        Upon successful payment and completion of a diagnostic survey,
        TalentSage grants the user (and sponsoring employer, where applicable) a
        perpetual, non-exclusive, non-transferable license to view, download,
        print, and utilize the generated PDF reports and interactive dashboards
        for internal professional development.
      </p>
    </section>

    <!-- 8 -->
    <section id="hitl">
      <h2>8. HR Employment Action Disclaimer &amp; Human-in-the-Loop (HITL) Protections</h2>
      <div class="plain">
        <span class="plain-label">In plain English</span>
        <p>
          Our diagnostic tools and AI debriefs are designed purely for
          development and learning support. Employers are legally required to
          maintain human oversight and review for employment actions.
        </p>
      </div>

      <h3>8.1 Human-in-the-Loop (HITL) Decision Support Mandate</h3>
      <p>
        All diagnostic engines, psychometric profiles, 360º scores, and
        AI-assisted debrief outputs function strictly as developmental
        decision-support tools. Sponsoring employers and administrators are
        legally required to maintain independent Human-in-the-Loop (HITL)
        oversight. TalentSage scores or report summaries shall NEVER serve as
        the sole automated basis for employment decisions, demotions, or
        recruitment outcomes.
      </p>
    </section>

    <!-- 9 -->
    <section id="privacy">
      <h2>9. Data Privacy, 18-Month Launch Retention &amp; Data Passport Transition</h2>
      <div class="plain">
        <span class="plain-label">In plain English</span>
        <p>
          Your survey data is kept identifiable for 18 months from your Survey
          Launch Date, after which it is automatically anonymized. Either your
          employer or we will remind you about your upcoming anonymization
          date. Before the anonymization date, you can convert your profile into
          an individual Data Passport Account to keep your reports.
        </p>
      </div>

      <h3>9.1 Privacy &amp; Retention Governance</h3>
      <p>
        Personal data retention, anonymization schedules, employer disclosure
        requirements and data processing roles are governed by our
        <router-link to="/privacy_page">Privacy Policy</router-link>.
      </p>

      <h3>9.2 Personal Data Passport Account Transition</h3>
      <p>
        Prior to enterprise account closure or scheduled data anonymization,
        eligible participants may convert their profile into a TalentSage
        personal Data Passport Account to preserve their historical diagnostic
        reports.
      </p>

      <h3>9.3 Individual Subscription Terms</h3>
      <p>
        Upon account conversion, the user contracts directly with TalentSage as
        an individual B2C account holder under Model 4 of these Terms. While
        converted Personal Data Passport Accounts provide read-only access to
        historical reports, continued active platform usage, including taking
        new assessments, generating new updated benchmarks or receiving new
        reports requires the purchase and maintenance of an active annual
        individual seat.
      </p>
    </section>

    <!-- 10 -->
    <section id="liability">
      <h2>10. Disclaimers, Liability Caps &amp; Multi-Jurisdiction Recourse</h2>
      <div class="plain">
        <span class="plain-label">In plain English</span>
        <p>
          If you ever have a privacy concern, contact our Head of Privacy at
          Privacy@TalentSage.com. Users in Europe can also contact DataRep.
          Unresolved privacy disputes can be submitted to BBB National Programs
          DPF Services for free independent dispute resolution, under US FTC
          oversight.
        </p>
      </div>

      <h3>10.1 Limitation of Liability</h3>
      <p>
        To the maximum extent permitted by law, TalentSage aggregate liability
        arising out of or related to platform usage shall not exceed the total
        fees paid by the user or sponsoring employer in the twelve (12) months
        preceding the claim.
      </p>

      <h3>10.2 Privacy Office &amp; Independent Recourse Architecture</h3>
      <p>
        All privacy-related inquiries, Subject Access Requests and Data Privacy
        Framework dispute mechanisms are governed by our
        <router-link to="/privacy_page">Privacy Policy</router-link>.
      </p>
    </section>
  </LegalPage>
</template>
